import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments';
import {
  OpportunityRequest,
  OpportunityResponse,
  CreateHoldersRequest,
  CreateHoldersResponse,
  ReferredResponse,
  SendReferredRequest,
  HumanValidationRequest,
  HumanValidationResponse,
  HumanVerificationRequest,
  ValidateClientResponse,
  StageValidationRequest,
  ValidateSsnResponse,
  CountryOption,
  PersonalDataRequest,
  PersonalDataResponse,
  StateOption,
  DocTypeOption,
  AddressMetadata,
  OccupationChoicesResponse,
  KycOccupationRequest,
  InvestmentKnowledgeChoicesResponse,
  InvestmentKnowledgeRequest,
  FinancialSituationChoicesResponse,
  FinancialStatusRequest,
  PepDataMetadata,
  SignAgreementRequest,
  SignAgreementResponse,
  AcceptanceSignRequest,
  AcceptanceDisclosureRequest,
  GeneratePasswordOtpRequest,
  GeneratePasswordOtpResponse,
  SavePasswordRequest,
  SavePasswordResponse,
  RecoveryRequest,
  RecoveryResponse,
  RecoveryValidateRequest,
  RecoveryValidateResponse
} from '@shared/interfaces/opportunity.interface';
import { OPP_ID_PREFIX, SKIP_LOADER_HEADER } from '@constants';

const URL_ONBOARDING = `${environment.apiPath}api/onboarding/${environment.version.onboarding}`;
const URL_TOOLBOX = `${environment.apiPath}api/toolbox/${environment.version.toolbox}`;

@Injectable({ providedIn: 'root' })
export class OnboardingApiService {
  private readonly http = inject(HttpClient);

  private get headers(): HttpHeaders {
    return new HttpHeaders({
      'Accept': 'application/json'
    });
  }

  generateOtp(data: OpportunityRequest): Observable<OpportunityResponse> {
    return this.http.post<OpportunityResponse>(
      `${URL_ONBOARDING}/opportunity/otp/generate`,
      data,
      { headers: this.headers }
    );
  }

  createHolders(data: CreateHoldersRequest): Observable<CreateHoldersResponse> {
    return this.http.post<CreateHoldersResponse>(
      `${URL_ONBOARDING}/opportunity/create`,
      data,
      { headers: this.headers }
    );
  }

  getAgreementDocument(): Observable<Blob> {
    return this.http.get(environment.apiAgrement, { responseType: 'blob' });
  }

  getReferred(): Observable<ReferredResponse> {
    return this.http.get<ReferredResponse>(
      `${URL_TOOLBOX}/choices/groups?group=REFERRED`,
      { headers: this.headers }
    );
  }

  sendReferred(data: SendReferredRequest): Observable<unknown> {
    return this.http.post(
      `${URL_ONBOARDING}/opportunity/referred`,
      data,
      { headers: this.headers }
    );
  }

  humanValidation(data: HumanValidationRequest): Observable<HumanValidationResponse> {
    return this.http.post<HumanValidationResponse>(
      `${URL_ONBOARDING}/opportunity/identity`,
      data,
      { headers: this.headers }
    );
  }

  humanVerification(data: HumanVerificationRequest): Observable<ValidateClientResponse> {
    return this.http.post<ValidateClientResponse>(
      `${URL_ONBOARDING}/opportunity/identity/verification`,
      data,
      { headers: this.headers }
    );
  }

  getCountries(): Observable<CountryOption[]> {
    return this.http.get<CountryOption[]>(
      `${URL_TOOLBOX}/countries`,
      { headers: this.headers }
    );
  }

  getPersonalDataChoices(): Observable<Record<string, unknown[]>> {
    return this.http.get<Record<string, unknown[]>>(
      `${URL_TOOLBOX}/choices/groups?group=PERSONAL_DATA`,
      { headers: this.headers }
    );
  }

  sendPersonalData(data: PersonalDataRequest): Observable<PersonalDataResponse> {
    return this.http.post<PersonalDataResponse>(
      `${URL_ONBOARDING}/opportunity/identity/confirmation`,
      data,
      { headers: this.headers }
    );
  }

  getStates(countryCode: string): Observable<StateOption[]> {
    return this.http.get<StateOption[]>(
      `${URL_TOOLBOX}/countries/${countryCode}/states`,
      { headers: this.headers }
    );
  }

  getDocTypes(): Observable<Record<string, DocTypeOption[]>> {
    return this.http.get<Record<string, DocTypeOption[]>>(
      `${URL_TOOLBOX}/choices/groups?group=CONTACT_DATA`,
      { headers: this.headers }
    );
  }

  // ── Stage Validation (pre-KYC) ──

  validateClient(data: StageValidationRequest): Observable<ValidateClientResponse> {
    return this.http.post<ValidateClientResponse>(
      `${URL_ONBOARDING}/opportunity/identity/client/validation`,
      data
    );
  }

  validateSsn(data: StageValidationRequest): Observable<ValidateSsnResponse> {
    return this.http.post<ValidateSsnResponse>(
      `${URL_ONBOARDING}/opportunity/validate/ssn`,
      data
    );
  }

  getOccupations(): Observable<OccupationChoicesResponse> {
    return this.http.get<OccupationChoicesResponse>(
      `${URL_TOOLBOX}/choices/groups?group=OCCUPATION`,
      { headers: this.headers }
    );
  }

  sendKycOccupation(data: KycOccupationRequest): Observable<unknown> {
    return this.http.post(
      `${URL_ONBOARDING}/kyc/occupation`,
      data
    );
  }

  getInvestmentKnowledgeChoices(): Observable<InvestmentKnowledgeChoicesResponse> {
    return this.http.get<InvestmentKnowledgeChoicesResponse>(
      `${URL_TOOLBOX}/choices/groups?group=INVESTMENT_KNOWLEDGE`,
      { headers: this.headers }
    );
  }

  sendInvestmentKnowledge(data: InvestmentKnowledgeRequest): Observable<unknown> {
    return this.http.post(
      `${URL_ONBOARDING}/kyc/investment_knowledge`,
      data
    );
  }

  getFinancialSituationChoices(): Observable<FinancialSituationChoicesResponse> {
    return this.http.get<FinancialSituationChoicesResponse>(
      `${URL_TOOLBOX}/choices/groups?group=FINANCIAL_SITUATION`,
      { headers: this.headers }
    );
  }

  sendFinancialStatus(data: FinancialStatusRequest): Observable<unknown> {
    return this.http.post(
      `${URL_ONBOARDING}/kyc/financial_status`,
      data
    );
  }

  // ── Create Password ──

  generatePasswordOtp(data: GeneratePasswordOtpRequest): Observable<GeneratePasswordOtpResponse> {
    return this.http.post<GeneratePasswordOtpResponse>(
      `${URL_ONBOARDING}/opportunity/user/generate`,
      data,
      { headers: this.headers }
    );
  }

  savePassword(data: SavePasswordRequest, keyToken2: string): Observable<SavePasswordResponse> {
    const headers = new HttpHeaders({
      'KeyToken2': keyToken2
    });
    return this.http.post<SavePasswordResponse>(
      `${URL_ONBOARDING}/opportunity/user/validate`,
      data,
      { headers }
    );
  }

  // ── Sign Contract (Adobe Sign) ──

  signAgreement(data: SignAgreementRequest): Observable<SignAgreementResponse> {
    return this.http.post<SignAgreementResponse>(
      `${URL_ONBOARDING}/agreement/sign`,
      data
    );
  }

  acceptanceSign(data: AcceptanceSignRequest): Observable<unknown> {
    return this.http.post(
      `${URL_ONBOARDING}/agreement/sign/acceptance`,
      data,
      { headers: new HttpHeaders({ [SKIP_LOADER_HEADER]: '1' }) }
    );
  }

  acceptanceDisclosure(data: AcceptanceDisclosureRequest): Observable<unknown> {
    return this.http.post(
      `${URL_ONBOARDING}/agreement/disclosure/acceptance`,
      data,
      { headers: new HttpHeaders({ [SKIP_LOADER_HEADER]: '1' }) }
    );
  }

  getDisclosureDocument(oppId: string): Observable<Blob> {
    return this.http.get(
      `${URL_ONBOARDING}/agreement/disclosure/document?oppId=${OPP_ID_PREFIX}${oppId}`,
      { responseType: 'blob', headers: new HttpHeaders({ [SKIP_LOADER_HEADER]: '1' }) }
    );
  }

  // ── Address ──

  // ── PEP Data ──

  sendPepData(metadata: PepDataMetadata, documentFile: File | null): Observable<unknown> {
    const formData = new FormData();
    formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    if (documentFile) {
      formData.append('document', documentFile, documentFile.name);
    }
    return this.http.post(
      `${URL_ONBOARDING}/kyc/pep`,
      formData,
      { headers: new HttpHeaders({ 'Accept': 'application/json' }) }
    );
  }

  // ── Address ──

  // ── Recovery ──

  recoveryOpportunity(data: RecoveryRequest): Observable<RecoveryResponse> {
    return this.http.post<RecoveryResponse>(
      `${URL_ONBOARDING}/opportunity/recovery`,
      data
    );
  }

  validateRecoveryOpportunity(data: RecoveryValidateRequest): Observable<RecoveryValidateResponse> {
    return this.http.post<RecoveryValidateResponse>(
      `${URL_ONBOARDING}/opportunity/recovery/validate`,
      data
    );
  }

  sendAddress(metadata: AddressMetadata, documentFile: File | null): Observable<unknown> {
    const formData = new FormData();
    formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    if (documentFile) {
      formData.append('document', documentFile);
    }
    return this.http.post(
      `${URL_ONBOARDING}/opportunity/address`,
      formData,
      { headers: new HttpHeaders({ 'Accept': 'application/json' }) }
    );
  }
}
